import InputAddressAutocompleteArea from "../ui/InputAddressAutocompleteArea.js";
import global from "../global.js";
import {Loader} from "google-maps";

const log = global.log.getLogger("f_mtac.view.OpenLayersDriverGM");
log.setLevel(log.levels.WARN);

/**
 * The OpenLayersDriver class is responsible for:
 *  - loading google map
 *  - interactions with Tacteam View class zoom and translations
 * The googlemap is embedded in an OpenLayers layer
 */
const OpenLayersDriverGoogle = function (view_, olDriver_) {
    log.debug("OpenLayersDriverGoogle", {olDriver_});

    if (global.default_configuration.gis.api_key === "") {
        log.error("An API key is mandatory to use Google Maps");
        return;
    }

    var view = view_;
    var olDriver = olDriver_; // OpenLayersDriver object
    var olMap = null; // OpenLayers Map
    var olView = null; // OpenLayers Map View
    var olGM = null; // OLGM handler for google maps
    var gmap; // the google map instance
    let marker = null;
    var GoogleLayer;
    var OLGoogleMaps;

    // Warning: OL zoom and GMap zoom may be different
    // Also, GMap terrain, satellite and roadmap max zoom are different
    // To avoid issues, the simpler is to limit the max zoom per map type
    // Test made in Versailles an in Salvizinet (France)
    const max_zoom_roadmap = 20; // max authorized zoom value
    const max_zoom_terrain = 20; // max authorized zoom value
    const max_zoom_satellite = 20; // max authorized zoom value
    const tile_size = 640;
    //const min_zoom = 1; // min authorized zoom value
    let mapTypeId = ""; // google.maps.MapTypeId ;

    /**
     * All properties
     */
    Object.defineProperties(this, {
        mapBasicLayerType: {
            set: (value) => {
                log.debug("set mapTypeId", {value});
                mapTypeId = value;
                if (olGM !== null) {
                    gmap.setMapTypeId(value);
                } else {
                    log.warn("Can't set mapType, olGM not instanciated");
                }
                olDriver.mapBasicLayerType = value;
            },
            get: () => mapTypeId
        },
        gmap: {
            set: () => {},
            get: () => gmap
        }
    });

    /**
     * Loads the Google Maps API
     * Requires an API key!
     */
    const load_api = async function () {
        log.debug("Trying to load Google Maps API...");
        try {
            const myloader = new Loader(global.default_configuration.gis.api_key, {
                version: "quarterly",
                libraries: ["places"]
            });
            log.debug(myloader);
            await myloader.load();
        } catch {
            // the error doesn't say much
            throw Error("Failed to load Google Maps");
        }
        log.debug("Successfully loaded Google Maps API");
    };

    /**
     * Loads OpenLayers - Google Maps integration (a.k.a. olgm) modules
     * Requires the Google Maps API to be loaded!
     */
    const load_modules = async function () {
        log.debug("Trying to import OpenLayers' Google Maps modules dynamically...");
        try {
            let {default: GoogleLayer_} = await import("olgm/layer/Google.js");
            GoogleLayer = GoogleLayer_;
            let {default: OLGoogleMaps_} = await import("olgm/OLGoogleMaps.js");
            OLGoogleMaps = OLGoogleMaps_;
        } catch (error) {
            log.error(error);
            throw Error("Failed dynamic import of OpenLayers' Google Maps modules");
        }
        log.debug("Successfully loaded modules olgm/layer/Google.js and olgm/OLGoogleMaps.js");
    };

    /**
     * Creates an Openlayers map
     * Creates an Openlayers layer with googlemap
     * Initiates Geocoding
     * When a map is defined in the background, the zoom and translations,
     *   events are got from Openlayers and a transformation is calculated
     *   and applied to SVG components (see Layer:render)
     * @returns {HTMLElement}
     */
    this.activate = async function () {
        // The API must be loaded before the modules
        await load_api();
        await load_modules();

        olMap = olDriver.ol_map;
        olView = olDriver.ol_map.getView();

        /**
         * When OpenLayers change:resolution event is fired,
         * listen to Google zoom-changed event to hide the SVG drawings when fired
         * listen to Google idle event to show the SVG drawings when fired
         */
        olView.on("change:resolution", function () {
            log.debug("change:resolution");
            if (olGM === null) {
                return;
            }
            window.google.maps.event.addListenerOnce(gmap, "zoom_changed", function () {
                log.debug("zoom_changed: ", {zoom: olView.getZoom()});
                olDriver.zoom_ongoing = true;
                view.hide_svg();
            });

            window.google.maps.event.addListenerOnce(gmap, "idle", function () {
                log.debug("google idle");
                olDriver.zoom_ongoing = false;
                olDriver.launch_transform();
                view.show_svg();
            });
        });

        global.olmap = olMap; // used for debug
    };

    const google_map_layer = ["roadmap", "satellite", "terrain"];

    /**
     * Add google map as an OpenLayers layer
     * Requires Google Maps API to be loaded!!!
     * @param type map Type, example maps.MapTypeId.ROADMAP
     * @returns {boolean} false in case of exception (google map not loaded)
     */
    this.add_map_layer = async function (type, retrying = false) {
        if (olGM != null) {
            log.debug("Google Maps already set as OpenLayers layer");
            return false;
        }

        if (olDriver.ol_map === null) {
            log.error("olMap is not activated");
            return false;
        }

        if (!window.google) {
            try {
                await this.activate();
            } catch (error) {
                log.error(error);
                return false;
            }
        }

        log.debug("add_google_map_layer", {type});
        olDriver.is_removing = false;
        if (type !== undefined && google_map_layer.includes(type)) {
            this.mapBasicLayerType = type;
        } else {
            this.mapBasicLayerType = window.google.maps.MapTypeId.ROADMAP;
        }

        try {
            olDriver.ol_map.addLayer(new GoogleLayer({mapTypeId: mapTypeId}));
            olGM = new OLGoogleMaps({map: olDriver.ol_map});
            olGM.activate();
            gmap = olGM.getGoogleMapsMap();
            gmap.setTilt(0); // to prevent 45° imagery on "SATELLITE" view

            let max_zoom = this.get_max_zoom();
            log.debug("max_zoom = ", max_zoom);
            olDriver.ol_map.getView().setMaxZoom(max_zoom);

            // save the basic layer
            olDriver.ol_basic_layer = olGM;

            global.gmap = gmap; // used for debug
        } catch (error) {
            log.error("Google map is not loaded", error);
            if (!retrying) {
                // Not sure it is usefull to retry automatically...
                log.warn("RETRYING...");
                window.google = undefined; // Reinitialize this so the activation can be retried.
                retrying = true;
                const returned = await this.add_map_layer(type, retrying);
                return returned;
            }
            return false;
        }
        log.debug("add_google_map_layer, out");
        return true;
    };

    /**
     * Remove the google map layer (when the space is closed)
     */
    this.remove_map_layer = function () {
        log.debug("remove_google_map_layer");
        if (olGM != null) {
            olDriver.is_removing = true;
            olGM.deactivate();
            let layers = olDriver.ol_map.getLayers().getArray();
            for (const layer of layers) {
                // find the google map layer and remove it
                if (layer.mapTypeId_ !== undefined) {
                    olDriver.ol_map.removeLayer(layer);
                }
            }
            olGM = null;
        }
    };

    /**
     * display marker on the last requested address
     */
    this.display_geocoding_address = function (address) {
        log.debug("display_geocoding_address", address);
        marker = new window.google.maps.Marker({
            position: {lat: address.location[1], lng: address.location[0]},
            gmap,
            title: address.name
        });
        marker.setMap(gmap);
    };

    /**
     * call the search address function
     **/
    this.search_address = async function () {
        let address = await InputAddressAutocompleteArea.interact();
        log.debug("geocoding", {address});
        return address;
    };

    /**
     * Remove geocoding address marker
     */
    this.remove_geocoding_address = function () {
        log.debug("remove_geocoding_address");
        // remove marker if any
        if (marker !== null) {
            marker.setMap(null);
        }
    };

    /**
     * create static images tiles
     * @returns array of blob
     */
    this.static_map_create_blob_tiles = async function (gis_state) {
        log.debug("gis_state ", gis_state);
        var url = "https://maps.googleapis.com/maps/api/staticmap";
        let zoom = gis_state.zoom;
        let tile_size = this.get_tile_size();
        let scale = 1;
        let key = global.default_configuration.gis.api_key;
        let tile_data_array = [];
        let blob_array = [];
        let tile_numbers = gis_state.tiles.length;

        for (let i = 0; i < tile_numbers; i++) {
            var data = {
                center: gis_state.tiles[i].center[1] + "," + gis_state.tiles[i].center[0],
                zoom: zoom,
                size: tile_size + "x" + tile_size,
                scale: scale,
                maptype: gis_state.mapType,
                key: key
            };
            var coordinates = gis_state.tiles[i].coordinates;
            tile_data_array.push({data, coordinates});
        }

        // call static map api to create cache file
        for (var index in tile_data_array) {
            var blob = await this.fetch_static_image(url, tile_data_array[index].data);
            blob_array.push({blob: blob, coordinates: tile_data_array[index].coordinates});
        }
        return blob_array;
    };

    this.fetch_static_image = async (url, data) => {
        return global.server.mtac_static_media_retrieve({url: url, data: data});
    };

    /**
     * Switch google map type from ROADMAP to SATELLITE to TERRAIN.
     * Change the maximum zoom authorized
     **/
    this.switch_map_basic_layer = function (fromAssistant) {
        if (olGM !== null) {
            switch (this.mapBasicLayerType) {
                case window.google.maps.MapTypeId.ROADMAP:
                    this.mapBasicLayerType = window.google.maps.MapTypeId.SATELLITE;
                    break;
                case window.google.maps.MapTypeId.SATELLITE:
                    this.mapBasicLayerType = window.google.maps.MapTypeId.TERRAIN;
                    break;
                default:
                    this.mapBasicLayerType = window.google.maps.MapTypeId.ROADMAP;
                    break;
            }

            gmap.setMapTypeId(mapTypeId);

            // si on n'est pas dans l'Assistant => créer le gis cache
            if (!fromAssistant) {
                let fallback_media_created = true;
                let object = global.scene.map.get_map_object();
                if (
                    (mapTypeId === window.google.maps.MapTypeId.ROADMAP && object.attributes.dataMediaLayer1 == undefined) ||
                    (mapTypeId === window.google.maps.MapTypeId.SATELLITE && object.attributes.dataMediaLayer2 == undefined) ||
                    (mapTypeId === window.google.maps.MapTypeId.TERRAIN && object.attributes.dataMediaLayer3 == undefined)
                ) {
                    fallback_media_created = false;
                }
                if (!fallback_media_created) {
                    global.scene.map.map_gis.create_gis_fallback_image(mapTypeId);
                } else {
                    log.debug("this.mapBasicLayerType ", this.mapBasicLayerType);
                    switch (this.mapBasicLayerType) {
                        case window.google.maps.MapTypeId.ROADMAP:
                            global.scene.map.set_map_object({
                                media_id: object.attributes.dataMediaLayer1
                            });
                            global.view.save_map_gis_context(object.attributes.dataMediaLayer1);
                            break;
                        case window.google.maps.MapTypeId.SATELLITE:
                            global.scene.map.set_map_object({
                                media_id: object.attributes.dataMediaLayer2
                            });
                            global.view.save_map_gis_context(object.attributes.dataMediaLayer2);
                            break;
                        case window.google.maps.MapTypeId.TERRAIN:
                            global.scene.map.set_map_object({
                                media_id: object.attributes.dataMediaLayer3
                            });
                            global.view.save_map_gis_context(object.attributes.dataMediaLayer3);
                            break;
                    }
                }
            }

            // change max zoom
            let max_zoom = this.get_max_zoom();
            olMap.getView().setMaxZoom(max_zoom);

            // change current zoom if necessary
            let current_zoom = olMap.getView().getZoom();
            if (current_zoom > max_zoom) {
                olMap.getView().setZoom(max_zoom);
            }
        }
    };

    this.update_map_basiclayer_type = function (type) {
        this.mapBasicLayerType = type;
    };

    /**
     * Get the max zoom values for a given map type
     * @returns {number} the max zoom value
     */
    this.get_max_zoom = function () {
        log.debug("get_max_zoom mapType = %s", mapTypeId);
        if (mapTypeId == window.google.maps.MapTypeId.ROADMAP) {
            return max_zoom_roadmap;
        }
        if (mapTypeId == window.google.maps.MapTypeId.TERRAIN) {
            return max_zoom_terrain;
        }
        return max_zoom_satellite;
    };

    /**
     * Get the tile size used in static map feature
     */
    this.get_tile_size = function () {
        return tile_size;
    };

    /**
     * set the fallback media id (used when offline or gis not authorized)
     */
    this.set_map_fallback_media = function (media_id, map_type, object) {
        if (map_type == window.google.maps.MapTypeId.ROADMAP && object.attributes.dataMediaLayer1 == undefined) {
            let media_layer1_id = {media_layer1_id: media_id};
            return global.scene.map.set_map_object(media_layer1_id);
        } else if (map_type == window.google.maps.MapTypeId.SATELLITE && object.attributes.dataMediaLayer2 == undefined) {
            let media_layer2_id = {media_layer2_id: media_id};
            return global.scene.map.set_map_object(media_layer2_id);
        } else if (map_type == window.google.maps.MapTypeId.TERRAIN && object.attributes.dataMediaLayer3 == undefined) {
            let media_layer3_id = {media_layer3_id: media_id};
            return global.scene.map.set_map_object(media_layer3_id);
        }
    };
};

export default OpenLayersDriverGoogle;
